/**
 * AURELIA — component-variant-picker.js
 * <variant-picker> — resolves option selections to a variant.
 *
 * Update strategy is deliberately two-phase:
 *
 *   1. Optimistic, from the in-page variant JSON. Price, availability, the
 *      button label, the selected media and the hidden variant id all update
 *      in the same frame as the click. Nothing waits on the network.
 *
 *   2. Authoritative, from the Section Rendering API. The same URL the
 *      customer would land on is re-rendered and the marked regions are
 *      swapped in. This is what keeps automatic discounts, unit pricing,
 *      per-variant metafields and Shopify Markets pricing correct — none of
 *      which can be derived client-side.
 *
 * If phase 2 fails, phase 1 has already produced a correct-looking, usable
 * page, and the variant id in the form is right, so checkout is unaffected.
 */
(function () {
  'use strict';

  const { Element, utils, config, emit } = window.Aurelia;

  class VariantPicker extends Element {
    mount() {
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.formId;
      this.productUrl = this.dataset.productUrl;
      this.shouldUpdateUrl = this.dataset.updateUrl !== 'false';

      this.variants = this.readVariants();
      if (this.variants.length === 0) return;

      this.listen(this, 'change', (event) => {
        if (!event.target.matches('[data-option-position]')) return;
        this.handleChange();
      });

      // The noscript fallback is meaningless now that this ran.
      const fallback = this.querySelector('noscript');
      if (fallback) fallback.remove();
    }

    readVariants() {
      const node = this.querySelector('[data-variant-data]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('[Aurelia] variant data is not valid JSON', error);
        return [];
      }
    }

    /** Selected value per option position, 1-indexed as Shopify numbers them. */
    get selectedOptions() {
      const values = [];

      this.$$('[data-option-position]').forEach((input) => {
        const position = Number(input.dataset.optionPosition);

        if (input.tagName === 'SELECT') {
          values[position - 1] = input.value;
        } else if (input.checked) {
          values[position - 1] = input.value;
        }
      });

      return values;
    }

    findVariant(options) {
      return (
        this.variants.find((variant) =>
          variant.options.every((value, index) => value === options[index])
        ) || null
      );
    }

    handleChange() {
      const options = this.selectedOptions;
      const variant = this.findVariant(options);

      this.updateOptionLabels();
      this.updateAvailability(options);

      // Phase 1 — instant.
      this.updateFormState(variant);
      this.updateMedia(variant);
      this.updateUrl(variant);

      emit('variant:change', {
        variant,
        sectionId: this.sectionId,
        productUrl: this.productUrl,
      });

      // Phase 2 — authoritative.
      if (variant) this.refreshFromServer(variant);
    }

    updateOptionLabels() {
      this.$$('.variant-option__fieldset').forEach((fieldset) => {
        const target = fieldset.querySelector('[data-option-value]');
        const checked = fieldset.querySelector('input:checked');
        if (target && checked) target.textContent = checked.value;
      });
    }

    /**
     * Marks combinations that do not exist or are sold out.
     * Each option is evaluated against the *other* current selections, which
     * is how a shopper reads it: "with this colour, which sizes can I get?"
     */
    updateAvailability(options) {
      this.$$('[data-option-position]').forEach((input) => {
        if (input.tagName === 'SELECT') return;

        const position = Number(input.dataset.optionPosition) - 1;
        const candidate = [...options];
        candidate[position] = input.value;

        const match = this.variants.find((variant) =>
          variant.options.every((value, index) => {
            // Positions after this one are not yet constrained.
            if (index > position && candidate[index] === undefined) return true;
            return value === candidate[index];
          })
        );

        const exists = Boolean(match);
        const available = Boolean(match && match.available);

        input.toggleAttribute('data-unavailable', !available);
        input.disabled = !exists;

        const label = this.querySelector(`label[for="${input.id}"]`);
        if (label) label.classList.toggle('variant-option__label--unavailable', !available);
      });
    }

    updateFormState(variant) {
      const form = document.getElementById(this.formId);
      const scope = form || document;

      const idInput = scope.querySelector('input[name="id"]');
      if (idInput) idInput.value = variant ? variant.id : '';

      const submit = scope.querySelector('[type="submit"][name="add"]');
      if (!submit) return;

      const label = submit.querySelector('.button__label');
      if (!variant) {
        submit.disabled = true;
        if (label) label.textContent = config.strings.unavailable;
        return;
      }

      submit.disabled = !variant.available;
      if (label) {
        label.textContent = variant.available ? config.strings.addToCart : config.strings.soldOut;
      }

      // Shopify's dynamic checkout button cannot represent a sold-out variant.
      const dynamicCheckout = scope.querySelector('.shopify-payment-button');
      if (dynamicCheckout) dynamicCheckout.hidden = !variant.available;
    }

    updateMedia(variant) {
      if (!variant || !variant.featured_media) return;
      emit('variant:media', {
        mediaId: variant.featured_media.id,
        sectionId: this.sectionId,
      });
    }

    updateUrl(variant) {
      if (!this.shouldUpdateUrl || !variant) return;

      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      // replaceState, not pushState: choosing a size should not add a history
      // entry the customer has to press Back through.
      window.history.replaceState({ variant: variant.id }, '', url.toString());
    }

    async refreshFromServer(variant) {
      // Supersede any in-flight refresh — only the newest selection matters.
      if (this.refreshController) this.refreshController.abort();
      this.refreshController = new AbortController();

      const url = new URL(this.productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);

      try {
        const html = await window.Aurelia.fetchSection(url.toString(), {
          sectionId: this.sectionId,
          signal: this.refreshController.signal,
        });

        const parsed = utils.parseHTML(html);

        // Only regions the theme explicitly marks are replaced. Anything the
        // customer may be interacting with — the picker itself, the quantity
        // field — is deliberately left alone.
        document.querySelectorAll(`[data-variant-update][data-section-id="${this.sectionId}"]`).forEach((node) => {
          const key = node.dataset.variantUpdate;
          const source = parsed.querySelector(
            `[data-variant-update="${key}"][data-section-id="${this.sectionId}"]`
          );
          if (source) node.innerHTML = source.innerHTML;
        });

        emit('variant:rendered', { variant, sectionId: this.sectionId });
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Phase 1 already produced a usable, correct-for-checkout state.
      }
    }
  }

  window.Aurelia.define('variant-picker', VariantPicker);
})();
